import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EMPTY, Observable, catchError, combineLatest, finalize, map, of, shareReplay, switchMap, timer } from 'rxjs';

import { AuthSessionService } from './auth-session.service';
import { FeatureFlagService } from './feature-flag.service';

/** CloudFront signed cookies expire at 10 min; refresh proactively a minute early. */
const NINE_MINUTES_MS = 9 * 60 * 1000;

/** Detected media-delivery mode, derived from the grant response status. */
export type MediaGrantMode = 'cookie' | 'signed_url' | 'unauthenticated' | 'unknown';

/** Optional body of POST /api/media/grant (only `expires_at` is read, informationally). */
interface MediaGrantBody {
  expires_at?: string | number;
}

/** Outcome of a grant refresh. `status` is the raw HTTP status (0 on transport error). */
export interface GrantResult {
  mode: MediaGrantMode;
  status: number;
}

/**
 * Owns the CloudFront signed-cookie *grant* lifecycle for the cookie-session
 * migration (AK-476 WP-F1).
 *
 * Per the API team's contract (AK-476, 2026-06-11/17):
 * - POST /api/media/grant (withCredentials). 200 → signed cookies written
 *   (cookie mode); 204 → signed_url mode, cookies not needed (successful no-op);
 *   401 → session expired (a clear needs-reauth signal — NOT a hard redirect
 *   from this library service in this slice).
 * - Grant on (a) app startup with a session, (b) proactively every ~9 min, and
 *   (c) reactively on a CDN 403 (re-grant then retry once).
 *
 * STRICTLY DORMANT when the master toggle is OFF (the default everywhere): the
 * proactive scheduler resolves to EMPTY, so no timer and no HTTP are created.
 * `refresh()` is single-flight — concurrent callers share one in-flight POST.
 */
@Injectable({ providedIn: 'root' })
export class MediaGrantService {
  /** Current detected delivery mode (signal, for reactive consumers). */
  private readonly _mode = signal<MediaGrantMode>('unknown');
  readonly mode = this._mode.asReadonly();

  /** Epoch ms when the current grant expires, if the body reported it. */
  private _expiresAt: number | null = null;

  /** Light telemetry — plain counters, no analytics dependency. */
  private _telemetry = {
    grantRefreshes: 0,
    grant204NoOps: 0,
    grant401s: 0,
    retrySuccesses: 0,
    retryFailures: 0,
  };

  /** Shared in-flight grant observable; null when no request is active. */
  private inFlight$: Observable<GrantResult> | null = null;

  constructor(
    private httpClient: HttpClient,
    private featureFlag: FeatureFlagService,
    private authSession: AuthSessionService,
  ) {
    this.startProactiveScheduler();
  }

  /** Read-only snapshot of the telemetry counters. */
  get telemetry(): Readonly<typeof this._telemetry> {
    return { ...this._telemetry };
  }

  /** Epoch ms of the current grant expiry, or null if unknown. */
  get expiresAt(): number | null {
    return this._expiresAt;
  }

  /**
   * Refresh the signed-cookie grant. SINGLE-FLIGHT: while a POST is in flight,
   * concurrent callers receive the same shared observable (one network call).
   * Never throws — a 401 or transport error resolves to a clear result.
   */
  refresh(): Observable<GrantResult> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.httpClient
      .post<MediaGrantBody>('/api/media/grant', null, { observe: 'response', withCredentials: true })
      .pipe(
        map((response) => this.handleGrantResponse(response)),
        catchError((error) => of(this.handleGrantError(error))),
        finalize(() => (this.inFlight$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.inFlight$;
  }

  /**
   * Programmatic media GET with a 403 → refresh → retry-once guard.
   * Toggle OFF → a plain single fetch (today's behaviour). Toggle ON → on a 403
   * the grant is refreshed once (shared single-flight) and the GET retried
   * exactly once; a second 403 returns that Response so the caller can show a
   * broken-media placeholder. One retry maximum.
   */
  async fetchWithGrant(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const request: RequestInit = { credentials: 'include', ...init };
    const response = await fetch(input, request);

    if (response.status !== 403 || !this.featureFlag.cookieSessionMigrationEnabled) {
      return response;
    }

    await this.refreshOnce();
    const retried = await fetch(input, request);
    if (retried.status === 403) {
      this._telemetry.retryFailures++;
    } else {
      this._telemetry.retrySuccesses++;
    }
    return retried;
  }

  /** Await a single refresh as a promise (used by the imperative retry surfaces). */
  refreshOnce(): Promise<GrantResult> {
    return new Promise((resolve) => {
      this.refresh().subscribe((result) => resolve(result));
    });
  }

  /**
   * Drop cached grant state so the next consumer re-grants from scratch. Called on
   * a tenant switch (AK-476 WP-F3): the server rotates the session and writes fresh
   * CloudFront cookies for the new entity in the switch response, so any cached
   * mode/expiry/in-flight grant is stale. The proactive scheduler is untouched —
   * it re-grants on the next session emission.
   */
  reset(): void {
    this._mode.set('unknown');
    this._expiresAt = null;
    this.inFlight$ = null;
  }

  private startProactiveScheduler(): void {
    combineLatest([this.featureFlag.cookieSessionMigrationEnabled$, this.authSession.session$])
      .pipe(
        // Dormant when OFF or unauthenticated: EMPTY creates no timer and no HTTP.
        // timer(0, …) fires immediately (startup grant) then every ~9 minutes.
        switchMap(([enabled, session]) => (enabled && session.authenticated ? timer(0, NINE_MINUTES_MS) : EMPTY)),
        switchMap(() => this.refresh()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private handleGrantResponse(response: HttpResponse<MediaGrantBody>): GrantResult {
    this._telemetry.grantRefreshes++;
    const expiresAt = response.body?.expires_at;
    this._expiresAt = expiresAt != null ? new Date(expiresAt).getTime() : null;

    if (response.status === 204) {
      this._telemetry.grant204NoOps++;
      this._mode.set('signed_url');
      console.debug('[MediaGrant] 204 — signed_url mode (no cookies needed)');
      return { mode: 'signed_url', status: 204 };
    }

    this._mode.set('cookie');
    console.debug('[MediaGrant] 200 — signed cookies written (cookie mode)');
    return { mode: 'cookie', status: 200 };
  }

  private handleGrantError(error: { status?: number }): GrantResult {
    const status = error?.status ?? 0;
    if (status === 401) {
      this._telemetry.grant401s++;
      this._mode.set('unauthenticated');
      console.warn('[MediaGrant] 401 — session expired (needs reauth)');
      return { mode: 'unauthenticated', status: 401 };
    }
    console.warn('[MediaGrant] grant failed; treating as unknown mode', error);
    return { mode: 'unknown', status };
  }
}
